import { IApplicationContext } from '../../ApplicationContext';
import {Search} from '../../filtering';
import {SuntekSettings, SystemSettings, User} from '../../ifmsTypes';
import {TableSettings} from '../../tableSettings';
import {IJobListViewContext, RightClickContext} from './JobListViewContext';

export interface ListOrderBy {
  orderBy: string,
  orderByAscending: boolean,
};

export interface ListCustomFilterRenderer {
  accessor: string,
  renderer: React.FunctionComponent|undefined,
  searchValueExtractor: (v:any|null|undefined) => any,
  displayValueExtractor?: (v:any|null|undefined) => any,
};

export interface ListRowRenderer {
  accessor: string,
  Cell: React.FunctionComponent,
  Header?: React.FunctionComponent|undefined,
};

export interface ExportRowRenderer {
  accessor: string,
  changedValue: (v:any|null|undefined) => any,
  backgroundColor?: (v:any|null|undefined) => any,
};

export interface DataFetchResult {
  data: Array<any>,
  totalCount: number,
  displayTotalCount?: number,
};

export interface ViewUrl {
  url: string,
  needAfterScreenUrl?: boolean,
  checkAllOperation?: boolean,
};

export default abstract class AbstractListView {
  systemSettings: SystemSettings|null = null;
  suntekSettings: SuntekSettings|null = null;
  user: User|null = null;

  /**
   * Set system settings so concrete implementation can easily access it, if needed
   *
   * @param {SystemSettings} s
   * @memberof AbstractListView
   */
  setSystemSettings(s:SystemSettings) {
    this.systemSettings = s;
  }

   /**
   * Set suntek settings so concrete implementation can easily access it, if needed
   *
   * @param {SuntekSettings} s
   * @memberof AbstractListView
   */
   setSuntekSettings(s:SuntekSettings) {
    this.suntekSettings = s;
  }

  /**
   * Set login user so concrete implementation can easily access it, if needed
   *
   * @param {User|null|undefined} u
   * @memberof AbstractListView
   */
  setUser(u:User|null|undefined) {
    this.user = u ?? null;
  }

  /**
   * Get table settings specific for implementation for SmartTable to render
   *
   * @abstract
   * @return {*}  {TableSettings}
   * @memberof AbstractListView
   */
  abstract getTableSettings(opType?: string, systemSettings?: SystemSettings, suntekSettings?: SuntekSettings): TableSettings;

  /**
   * Gets default order by for screen
   *
   * @abstract
   * @return {*}  {ListOrderBy}
   * @memberof AbstractListView
   */
  abstract getDefaultOrderBy(search?: any): ListOrderBy;

  /**
   * Gets custom filter renderers, if any
   *
   * @abstract
   * @return {*}  {Array<ListCustomFilterRenderer>}
   * @memberof AbstractListView
   */
  abstract getCustomFilterRenderers(opType?: string, systemSettings?: SystemSettings, suntekSettings?: SuntekSettings): Array<ListCustomFilterRenderer>;

  /**
   * Gets row renderer definitions for all columns inside table settings for concrete implementation
   *
   * @abstract
   * @return {*}  {Array<ListRowRenderer>}
   * @memberof AbstractListView
   */
  abstract getRowRenderers(opType?: string, systemSettings?: SystemSettings, suntekSettings?: SuntekSettings, listCount?: number, useDarkMode?: boolean): Array<ListRowRenderer>;

  /**
   * Provides data array, usually returned from the api
   *
   * @abstract
   * @param {*} api
   * @param {Search} search
   * @param {string} opType
   * @return {*}  {Promise<DataFetchResult>}
   * @memberof AbstractListView
   */
  abstract fetchAndFormatData(api: any, search:Search, pageNum: number, limit: number, opType: string|undefined, user?: User|null, jobListViewContext?: IJobListViewContext|null, showLoading?: boolean) : Promise<DataFetchResult>;

    /**
   * Provides data array, usually returned from the api
   *
   * @abstract
   * @param {*} api
   * @param {Search} search
   * @param {string} opType
   * @return {*}  {Promise<Array<any>>}
   * @memberof AbstractListView
  */  
  abstract exportFormatDataWithExcel(api: any, search:Search, pageNum: number, limit: number, pageSize: number, pagePerBatch: number, opType: string|undefined, user?: User|null, jobListContext?: IJobListViewContext|null) : Promise<Array<any>>;

  /**
   * Gets rendered commands (action buttons) for concrete implementation, which are rendered on top left bar, right next to Job title
   *
   * @abstract
   * @return {*}  {React.ReactNode}
   * @memberof AbstractListView
   */
  abstract renderCommands(appContext?: IApplicationContext) : React.ReactNode;
  /**
   * Gets rendered commands (action buttons) for concrete implementation, which are rendered on top left bar, right to the Close button.
   * By default, screens don't render anything
   *
   * @abstract
   * @return {*}  {React.ReactNode}
   * @memberof AbstractListView
   */
   public renderAdditionalCommands() : React.ReactNode | null {
    return null;
  }

  /**
   * Gets additional rendered node, which is rendered before list columns "Configure" button
   *
   * @abstract
   * @return {*}  {React.ReactNode}
   * @memberof AbstractListView
   */
  abstract renderBeforeConfigureButton() : React.ReactNode;

  /**
   * Gets extra rendered node (or empty if it does not exist), which is rendered after table list.
   * If concrete implementation has it, here is the place to render InfoArea drawer
   *
   * @abstract
   * @return {*}  {React.ReactNode}
   * @memberof AbstractListView
   */
  abstract renderExtras(hideLogHistory?: boolean): React.ReactNode;

  /**
   * Get view url for row
   *
   * @abstract
   * @param {*} row
   * @memberof AbstractListView
   */
  abstract getViewUrlFor(row: any): ViewUrl | string;


  /**
   * In case list screen has popup for View, it should return true.
   * In that case, needed info will be stored in job list context for client to use in appropriate way.
   *
   * @return {*}  {boolean}
   * @memberof AbstractListView
   */
  hasViewPopup(): boolean {
    return false;
  }


  /**
   * Gets list view title. Receives the operation (as defined in constants) as a parameter so the list can customize
   * the title based on given context.
   *
   * @abstract
   * @return {*}  {string}
   * @memberof AbstractListView
   */
  abstract getListViewTitle(opType: string|undefined): string;


  /**
   * Row can require some extra rendering. For example, when cell is clicked to open dialog with details
   *
   * @abstract
   * @return {*}  {React.ReactNode|null}
   * @memberof AbstractListView
   */
   renderRowExtras(rowId: number|null, context: IJobListViewContext, cell: any, applicationContext:any) : React.ReactNode|null {
     return null;
   }
   

   /**
   * List view can inject content inside top right operation menu (before Settings)
   *
   * @abstract
   * @return {*}  {React.ReactNode|null}
   * @memberof AbstractListView
   */
    renderInsideTopRightOpMenu(context: IJobListViewContext) : React.ReactNode|null {
      return null;
    }

   /**
    * If list view has right click context to render
    *
    * @return {*}  {boolean}
    * @memberof AbstractListView
    */
   hasRightClickContext(): boolean {
     return false;
   }


   /**
   * List can choose to display additional context (for example menu) on cell right click
   *
   * @abstract
   * @return {*}  {React.ReactNode|null}
   * @memberof AbstractListView
   */
    renderRightClickContext(context: RightClickContext, dismiss: VoidFunction) : React.ReactNode|null {
      return null;
    }

    /**
     * Returns if list screen has memo button. True by default, can be overridden
     * @returns 
     */
    hasMemo(): boolean {
      return true;
    }

    useLogHistory(): boolean {
      return false;
    }

      /**
   * Get Utilities(MailFaxPrint) open url for row
   */
  getOpenUrlFor(row: any): string{
    return '';
  };

  public commandsAfterFilterBtn() : React.ReactNode | null {
    return null;
  }

  transactionImpactBtn(userId: string): React.ReactNode {
    return null;
  }

  resizetitleLabel(): boolean{
    return false;
  }
}
